import { Eye, Loader2 } from "lucide-react";
import { useState, type MouseEvent } from "react";

import { cn } from "@/lib/utils";

import { canPreview, FilePreviewDialog, type PreviewFile } from "./FilePreviewDialog";
import { threadFileUrl } from "./files";
import { findMentions, resolveMention } from "./mention";

/** A file a mention can mean: one attached to the same draft or message. */
export interface MentionFile {
  id: number;
  name: string;
  contentType: string;
}

/**
 * The mention's look, in the composer and the sent message alike: a square-ish highlight that
 * keeps the text's own width (no horizontal padding, no border), so the composer's overlay
 * lines up with the text under it.
 */
export const MENTION_CHIP =
  "rounded-[2px] bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.25)] [box-decoration-break:clone]";

/** The square the eye sits in: over the "@" in the composer (opaque, tinted like the chip). */
const OVER_AT =
  "absolute inset-0 flex items-center justify-center rounded-[2px] bg-card text-primary shadow-[inset_0_0_0_99px_hsl(var(--primary)/0.1)]";
/** In a sent message, in place of the "@". */
const INLINE =
  "inline-flex h-[1em] w-[1em] items-center justify-center align-[-0.1em] text-primary";

/** The mini eye where the "@" is; pending (a document still linking), a spinner. */
export function MentionEye({
  name,
  over = false,
  pending = false,
  onPreview,
}: {
  name: string;
  /** Laid over the "@" of the composer's text, as a pointer shortcut: the chip's eye is the control. */
  over?: boolean;
  pending?: boolean;
  onPreview?: () => void;
}) {
  const square = over ? OVER_AT : INLINE;
  if (pending) {
    return (
      <span className={square}>
        <Loader2 className="size-3 motion-safe:animate-spin" aria-label="Adding" />
      </span>
    );
  }
  if (!onPreview) return over ? null : <span className={square}>@</span>;
  return (
    <button
      type="button"
      tabIndex={over ? -1 : undefined}
      aria-label={`Preview ${name}`}
      // Phones: a finger-sized target around the small square, with no layout of its own.
      className={cn(
        square,
        "cursor-pointer rounded-[2px] before:absolute before:-inset-2 before:content-[''] hover:bg-accent",
        over ? "pointer-events-auto" : "relative"
      )}
      // The composer keeps its caret.
      onMouseDown={(event: MouseEvent) => event.preventDefault()}
      onClick={onPreview}
    >
      <Eye className="size-3" aria-hidden />
    </button>
  );
}

/** One mention in a sent message: the highlight, the eye in place of the "@", the name. */
function MessageMention({ file, threadId }: { file: MentionFile; threadId: number | null }) {
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  const open = async () => {
    const url = threadId == null ? null : await threadFileUrl(threadId, file.id);
    if (url) setPreview({ name: file.name, url, contentType: file.contentType });
  };
  return (
    <span className={MENTION_CHIP}>
      <MentionEye
        name={file.name}
        onPreview={canPreview(file.contentType) ? () => void open() : undefined}
      />
      {file.name}
      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
    </span>
  );
}

/**
 * A sent message's text with its mentions as chips: only names of the message's own files are
 * mentions (the first of a name, as in the composer); any other "@..." is just text.
 */
export function MentionText({
  text,
  files,
  threadId,
}: {
  text: string;
  files: MentionFile[];
  threadId: number | null;
}) {
  const tokens = findMentions(
    text,
    files.map((file) => file.name)
  );
  if (!tokens.length) return <>{text}</>;
  const nodes = [];
  let from = 0;
  for (const token of tokens) {
    nodes.push(text.slice(from, token.start));
    const file = resolveMention(files, token.name)!;
    nodes.push(<MessageMention key={token.start} file={file} threadId={threadId} />);
    from = token.end;
  }
  nodes.push(text.slice(from));
  return <>{nodes}</>;
}
